import { useState, useEffect, useRef } from "react";
import { useWizard } from "@/contexts/WizardContext";
import { LockedBanner } from "../common/LockedBanner";
import { useToast } from "@/hooks/use-toast";
import { CompanyIdentityForm } from "./CompanyIdentityForm";
import { LegalStructureForm } from "./LegalStructureForm";
import { FinancialInfoForm } from "./FinancialInfoForm";
import { WorkforceForm } from "./WorkforceForm";
import { LocationForm } from "./LocationForm";
import { CardPanelHeader } from "../common/CardPanelHeader";
import { ProgressCard } from "../common/ProgressCard";

const IMAGE_MIMES = ['image/jpeg', 'image/png'];
const MAX_FILE_BYTES = 10 * 1024 * 1024;

export function GeneralInfoSection({ onClose }: { onClose: () => void }) {
  const { generalInfo, updateGeneralInfo, uploadFile, forceSave, saveStatus, isLocked, isReadOnly, generalInfoCardCompletion } = useWizard();
  const disabled = isLocked || isReadOnly;
  const { toast } = useToast();
  const [isMapDialogOpen, setIsMapDialogOpen] = useState(false);
  const [pendingMapCoords, setPendingMapCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [latInputValue, setLatInputValue] = useState("");
  const [lngInputValue, setLngInputValue] = useState("");
  const [coordsFromMapClick, setCoordsFromMapClick] = useState(false);
  const [currentSiteIndex, setCurrentSiteIndex] = useState<number | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Sync text inputs when coordinates change from map click
  useEffect(() => {
    if (coordsFromMapClick && pendingMapCoords) {
      setLatInputValue(pendingMapCoords.lat.toString());
      setLngInputValue(pendingMapCoords.lng.toString());
      setCoordsFromMapClick(false);
    }
  }, [pendingMapCoords, coordsFromMapClick]);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!IMAGE_MIMES.includes(file.type)) {
      toast({
        title: "Unsupported file type",
        description: "Only JPEG or PNG files are accepted.",
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_BYTES) {
      toast({
        title: "File too large",
        description: "Maximum file size is 10 MB.",
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }

    const result = await uploadFile(file, 'logo');
    if (result.ok) {
      updateGeneralInfo({ companyLogoUrl: result.data });
      toast({ title: "Logo uploaded", description: file.name });
    } else {
      toast({ title: "Upload failed", description: result.error, variant: "destructive" });
    }
    e.target.value = "";
  };

  return (
    <section className="space-y-6" data-testid="card-general-information">
      <CardPanelHeader
        tag={"General Information"}
        title={"General Information"}
        description="Tell us about your company so we can understand your business and tailor the assessment."
        onClose={onClose}
        onSave={forceSave}
        saveStatus={saveStatus}
        disabled={disabled}
      />

      <div className="space-y-6">
        {disabled && <LockedBanner />}

        <CompanyIdentityForm
          generalInfo={generalInfo}
          updateGeneralInfo={updateGeneralInfo}
          logoInputRef={logoInputRef}
          onLogoUpload={handleLogoUpload}
          isLocked={disabled}
        />

        <LocationForm
          generalInfo={generalInfo}
          updateGeneralInfo={updateGeneralInfo}
          isMapDialogOpen={isMapDialogOpen}
          setIsMapDialogOpen={setIsMapDialogOpen}
          pendingMapCoords={pendingMapCoords}
          setPendingMapCoords={setPendingMapCoords}
          latInputValue={latInputValue}
          setLatInputValue={setLatInputValue}
          lngInputValue={lngInputValue}
          setLngInputValue={setLngInputValue}
          coordsFromMapClick={coordsFromMapClick}
          setCoordsFromMapClick={setCoordsFromMapClick}
          currentSiteIndex={currentSiteIndex}
          setCurrentSiteIndex={setCurrentSiteIndex}
          isLocked={disabled}
        />

        <LegalStructureForm
          generalInfo={generalInfo}
          updateGeneralInfo={updateGeneralInfo}
          isLocked={disabled}
        />

        <FinancialInfoForm
          generalInfo={generalInfo}
          updateGeneralInfo={updateGeneralInfo}
          isLocked={disabled}
        />

        <WorkforceForm
          generalInfo={generalInfo}
          updateGeneralInfo={updateGeneralInfo}
          isLocked={disabled}
        />

        <ProgressCard completion={generalInfoCardCompletion} />
      </div>
    </section>
  );
}
